import { abbreviateNumber } from '@src/utils/data';
import { DataCard } from '@Components/DataCard';
import { AmcNewToBrandCostOverview, AmcNewToBrandRevenueOverview } from './amc-new-to-brand-types';

type RevenueCardData = AmcNewToBrandRevenueOverview & { ntbPurchaseRate: number };

// Spend-based efficiency ratios (all pair an "All" figure against its NTB-only counterpart)
// first, then absolute NTB outcome numbers.
const CARD_KEYS = [
  'spend',
  'cpaAll',
  'cpaNtb',
  'roasAll',
  'ntbRoas',
  'ntbCustomers',
  'ntbSales',
  'ntbPurchaseRate'
];

// Money fields render with a $ prefix; the purchase rate renders with a % suffix; everything
// else is abbreviated (1234 -> 1.2k).
const MONEY_KEYS = new Set(['spend', 'cpaNtb', 'cpaAll', 'ntbSales']);

const formatCardValue = (key: string, value: number): string => {
  if (MONEY_KEYS.has(key)) return `$${abbreviateNumber(value)}`;
  if (key === 'ntbPurchaseRate') return `${abbreviateNumber(value)}%`;
  return String(abbreviateNumber(value));
};

// ntbRoas is the only field whose camelCase order (NTB first) doesn't match its "All" pair —
// cpaAll/cpaNtb and roasAll both put the qualifier last, so override this one to match.
const LABEL_OVERRIDES: Record<string, string> = {
  ntbRoas: 'ROAS NTB'
};

// camelCase field names with domain acronyms (NTB, ROAS, CPA) that `inflected`'s humanize
// (built for snake_case) wouldn't split or capitalize correctly — same approach as
// AmcGeoDataCards' formatCardLabel.
const formatCardLabel = (key: string): string =>
  LABEL_OVERRIDES[key] ??
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/ntb/i, 'NTB')
    .replace(/roas/i, 'ROAS')
    .replace(/cpa/i, 'CPA');

interface AmcNewToBrandDataCardRowProps {
  costCardData: AmcNewToBrandCostOverview;
  revenueCardData: RevenueCardData;
}

export const AmcNewToBrandDataCardRow = ({
  costCardData,
  revenueCardData
}: AmcNewToBrandDataCardRowProps) => {
  const allCardData: Record<string, number> = { ...costCardData, ...revenueCardData };

  return (
    <div className='data-card-row'>
      {CARD_KEYS.map(key => (
        <DataCard
          key={key}
          label={formatCardLabel(key)}
          value={formatCardValue(key, allCardData[key])}
          width='100%'
        />
      ))}
    </div>
  );
};
